import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../../lib/api';
import {
  Users, Gavel, Activity, Settings, ShieldCheck,
  AlertTriangle, CheckCircle2, UserX, MailWarning, ArrowRight,
} from 'lucide-react';

const KPI = ({ icon: Icon, label, value, tone = 'default', onClick, testId }) => {
  const tones = {
    default: 'bg-white border-slate-200',
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
  };
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testId}
      className={`text-left rounded-sm border p-4 transition-shadow ${tones[tone]} ${onClick ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500">
        <Icon size={13} /> {label}
      </div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#3b4872]">{value ?? '—'}</div>
    </button>
  );
};

const QuickLink = ({ icon: Icon, label, desc, to, testId }) => {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(to)} data-testid={testId}
      className="text-left rounded-sm border border-slate-200 bg-white p-4 hover:shadow-md hover:border-[#3b4872] transition-all w-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-sm bg-[#3b4872] p-2 text-white"><Icon size={18} /></div>
          <div>
            <div className="font-display font-bold text-[#3b4872]">{label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
          </div>
        </div>
        <ArrowRight size={16} className="text-slate-400" />
      </div>
    </button>
  );
};

export default function VendorPortalOverview() {
  const [h, setH] = useState(null);

  useEffect(() => {
    apiGet('/vendor-portal-admin/diagnostics/health').then(setH).catch(() => {});
  }, []);

  return (
    <div data-testid="vendor-portal-overview" className="space-y-6">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2 flex items-center gap-2">
          <ShieldCheck size={14} /> Vendor Portal Admin
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Overview</h1>
        <p className="text-sm text-slate-600 mt-1">Superadmin control panel · Account suspension · Diagnostics · Portal settings</p>
      </div>

      {/* Health KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPI icon={Users} label="Total Accounts" value={h?.total_accounts} testId="kpi-total" />
        <KPI icon={CheckCircle2} label="Active" value={h?.active} tone="green" testId="kpi-active" />
        <KPI icon={MailWarning} label="Pending Verification" value={h?.pending_verification}
          tone={h?.pending_verification > 0 ? 'amber' : 'default'} testId="kpi-pending" />
        <KPI icon={UserX} label="Suspended" value={h?.suspended}
          tone={h?.suspended > 0 ? 'red' : 'default'} testId="kpi-suspended" />
        <KPI icon={Gavel} label="Live Auctions" value={h?.live_auctions}
          tone={h?.live_auctions > 0 ? 'blue' : 'default'} testId="kpi-live-auctions" />
        <KPI icon={Activity} label="Open RFQs" value={h?.open_rfqs} testId="kpi-open-rfqs" />
        <KPI icon={AlertTriangle} label="Failed Logins (24h)" value={h?.failed_logins_24h}
          tone={h?.failed_logins_24h > 5 ? 'amber' : 'default'} testId="kpi-failed-logins" />
      </div>

      {/* Quick links */}
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500 mb-3">Modules</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <QuickLink icon={Users} label="Vendor Accounts" desc="Suspend, reactivate, reset password, force-verify email"
            to="/app/vendor-portal-admin/accounts" testId="ql-accounts" />
          <QuickLink icon={Activity} label="Diagnostics & Logs" desc="Login attempts, admin audit trail"
            to="/app/vendor-portal-admin/diagnostics" testId="ql-diagnostics" />
          <QuickLink icon={Settings} label="Portal Settings" desc="Maintenance mode, registration toggle, lockout config"
            to="/app/vendor-portal-admin/settings" testId="ql-settings" />
          <QuickLink icon={Gavel} label="Auctions Admin" desc="Reverse-auction management (Phase B — coming soon)"
            to="/app/vendor-portal-admin/auctions" testId="ql-auctions" />
        </div>
      </div>
    </div>
  );
}
